<template>
  <q-page>
    <div class="row q-pt-lg q-pb-lg justify-center" ref="top">
      <div class="col-12 text-center q-pt-xl title">
        <p style="font-family: 'Arima'; opacity: 0.7">
          {{ $t('login.description') }}
        </p>
      </div>
      <div class="col-10 q-pb-sm">
        <form>
          <q-field
            icon="mail"
            :label="$t('login.email')"
            icon-color="primary"
            :error-label="$t('login.errorValidationEmail')"
            borderless
          >
            <q-input
              type="email"
              v-model="form.email"
              @change="v$.form.email.$touch"
              @blur.stop="$helpers.scrollToElement($refs.pws.$el)"
              @keyup.enter="$helpers.scrollToElement($refs.pws.$el)"
              :error="v$.form.email.$error"
              class="full-width"
              autocomplete="username"
            />
          </q-field>
          <q-field
            icon="lock"
            :label="$t('login.password')"
            icon-color="primary"
            :error-label="$t('login.errorValidationPassword')"
            borderless
          >
            <q-input
              ref="pws"
              type="password"
              v-model="form.pass"
              @change="v$.form.pass.$touch"
              @blur.stop="$helpers.scrollToElement($refs.top)"
              @keyup.enter="() => submit()"
              :error="v$.form.pass.$error"
              class="full-width"
              autocomplete="current-password"
            />
          </q-field>
          <div class="forgottenPass">
            <q-btn
              @click="forgottenPassword = true"
              no-caps
              flat
              dense
              :label="`${$t('login.forgottenPassword')} ?`"
            />
          </div>
        </form>
      </div>
      <div>
        <q-dialog v-model="forgottenPassword" persistent>
          <q-card style="min-width: 350px">
            <q-card-section>
              <p style="font-family: 'Arima'; opacity: 0.7">
                {{ $t('login.tapEmail') }} :
              </p>
            </q-card-section>

            <q-card-section class="q-pt-none">
              <q-input
                dense
                v-model="form.email"
                :placeholder="$t('login.email')"
                :error="v$.form.email.$error"
                autofocus
                @keyup.enter="resetPassword"
              />
            </q-card-section>

            <q-card-actions align="right">
              <q-btn outline :label="$t('common.cancel')" v-close-popup />
              <q-btn
                color="primary"
                :disable="form.email"
                :label="$t('common.send')"
                @click="resetPassword"
              />
            </q-card-actions>
          </q-card>
        </q-dialog>
      </div>
      <div class="col-12 text-center q-pl-md q-pr-md">
        <div class="full-width">
          <q-btn
            class="button-width full-width"
            color="primary"
            @click="() => submit()"
          >
            {{ $t('login.logIn') }}
          </q-btn>
        </div>
        <div class="separator">{{ $t('login.or') }}</div>
        <div class="row">
          <div class="row justify-center full-width">
            <q-btn
              class="row button-width full-width justify-between bsk-btn bsk-btn-default"
              unelevated
              @click="() => msalSubmit()"
            >
              <q-icon size="sm" color="accent">
                <img src="~assets/ms-pictogram.svg" color="primary" />
              </q-icon>
              <span class="col">{{ $t('login.connectWithMicrosoft') }}</span>
            </q-btn>
          </div>
        </div>
        <!-- Magiclink entry point. The "Mot de passe oublié ?" dialog above keeps
             using the existing OTP flow - the two are deliberately separate. -->
        <div class="separator">
          {{ $t('login.firstConnectionQuestion') }}
        </div>
        <div class="row justify-center full-width">
          <q-btn
            class="row button-width full-width justify-center bsk-btn bsk-btn-default first-login-button"
            unelevated
            no-caps
            data-cy="activate-account"
            @click="goToFirstLogin"
          >
            <span class="col">{{ $t('login.activateAccount') }}</span>
          </q-btn>
        </div>
      </div>
    </div>
  </q-page>
</template>

<script>
import useVuelidate from '@vuelidate/core'
import { useQuasar } from 'quasar'
import { required, email, minLength } from '@vuelidate/validators'
import eventBus from 'src/eventBus'
import { mapGetters } from 'vuex'
import dayjs from 'dayjs'

export default {
  inject: ['$helpers', '$DB', '$msal'],
  setup() {
    return { v$: useVuelidate(), q: useQuasar() }
  },
  mounted() {
    this.$q.loading.hide()
    window.addEventListener('deviceready', this.deviceReady(), false)
  },
  validations() {
    return {
      form: {
        email: { required, email },
        pass: { required, minLength: minLength(6) }
      }
    }
  },
  data() {
    return {
      forgottenPassword: false,
      form: {
        email: '',
        pass: ''
      },
      device: {
        manufacturer: '',
        model: '',
        platform: '',
        version: ''
      },
      // TODO: Implement redirections from this.$route.query
      nextRoute: { path: '/categories' },
      appVersion: ''
    }
  },
  computed: {
    ...mapGetters({
      appRestartRequired: 'common/appRestartRequired'
    }),
    hasPopulatedCredentials() {
      return (
        this.$q.localStorage.has('credentials') &&
        !!this.$q.localStorage.getItem('credentials').provider
      )
    },
    isReturningMsalUser() {
      return (
        this.hasPopulatedCredentials &&
        this.$q.localStorage.getItem('credentials').provider === 'MSAL' &&
        this.$q.localStorage.getItem('credentials').email.length
      )
    },
    isReturningSoWellUser() {
      return (
        this.hasPopulatedCredentials &&
        this.$q.localStorage.getItem('credentials').provider === 'SoWell' &&
        this.$q.localStorage.getItem('credentials').email.length &&
        this.$q.localStorage.getItem('credentials').password.length
      )
    }
  },
  methods: {
    goToFirstLogin() {
      this.$router.push({
        path: '/first-login',
        query: { context: 'activate' }
      })
    },
    async resetCache() {
      console.info('resetCache')
      await this.$msal.signOut()
      this.$store.commit('audits/RESET_CURRENT_OTHER_CONTROL')
      this.$store.dispatch('reporter/reset')
      await this.$DB.resetLocalDBs(this.$q.platform.is.capacitor)
      await this.$patrimoine.resetLocalDBs(this.$q.platform.is.capacitor)
    },
    async handleUserChange(currentEmail, cacheAlreadyReseted = false) {
      console.info('handleUserChange')
      const isDifferentUser =
        !this.$q.localStorage.has('credentials') ||
        this.$q.localStorage.getItem('credentials').email !== currentEmail
      if (isDifferentUser) {
        this.$q.localStorage.clear()
        if (!cacheAlreadyReseted) {
          await this.resetCache()
        }
      }
    },
    async deviceReady() {
      this.hideSplashScreen()
      await this.getDeviceInfo()
      await this.tryAutoLogin()
    },
    resetPassword() {
      this.v$.form.email.$touch()
      if (!this.v$.form.email.$error) {
        this.$SowellProxy
          .resetPassword(this.form.email)
          .then(() => {
            this.$q.notify({
              message: this.$t('login.mailSent'),
              type: 'info',
              position: 'top'
            })
            this.forgottenPassword = false
          })
          .catch((error) => {
            this.$q.notify({
              message: this.$t('login.mailNotSent'),
              type: 'warning',
              position: 'top'
            })
            this.forgottenPassword = false
          })
      }
    },
    async submit(isAutoLogin = false) {
      this.v$.form.$touch()

      if (this.v$.form.$error) {
        this.$q.notify({
          message: this.$t('login.connectErrors'),
          type: 'warning',
          position: 'top'
        })
      } else {
        try {
          this.authenticate(
            'SoWell',
            this.form.email,
            this.form.pass,
            isAutoLogin
          )
        } catch (error) {
          if (isAutoLogin) {
            this.$q.localStorage.remove('credentials')
          }
          throw error
        }
      }
    },
    async msalSubmit(isAutoLogin = false) {
      try {
        const result = await this.$msal.mobileSignIn()
        this.authenticate('MSAL', result.email, result.token, isAutoLogin)
      } catch (error) {
        if (isAutoLogin) {
          this.$q.localStorage.remove('credentials')
        }
        await this.$msal.signOut()
        throw error
      }
    },

    // ************* Local helpers ****************
    async hideSplashScreen() {
      const { SplashScreen } = await import(
        'Capacitor/@capacitor/splash-screen'
      )
      await SplashScreen.hide()
    },
    async getDeviceInfo() {
      const { Device } = await import('Capacitor/@capacitor/device')
      await Device.getInfo().then((info) => {
        this.device = {
          manufacturer: info.manufacturer,
          model: info.model,
          platform: info.platform,
          version: info.osVersion
        }
      })
    },
    async tryAutoLogin() {
      this.$q.loading.show()
      try {
        if (this.isReturningMsalUser) {
          const credentials = this.$q.localStorage.getItem('credentials')
          const expiresAt = dayjs(credentials.expiresAt)
          if (expiresAt.isAfter(dayjs())) {
            this.msalSubmit(true)
          } else {
            this.$q.localStorage.remove('credentials')
            await this.$msal.signOut()
            this.handleReset()
          }

        } else if (this.isReturningSoWellUser) {
          const credentials = this.$q.localStorage.getItem('credentials')
          this.form.email = credentials.email
          this.form.pass = credentials.password
          this.submit(true)
        }
      } catch (error) {
        console.error('tryAutoLogin error', error)
      } finally {
        this.$q.loading.hide()
      }
    },
    handleLoginRedirect(companyHomepage, userRoles) {
      if ('redirect' in this.$route.query) {
        this.$router.push(this.$route.query.redirect)
      } else {
        switch (companyHomepage) {
          case 1:
            if (userRoles.includes('checklister'))
              this.nextRoute = { path: '/zones' }
            break
          case 2:
            this.nextRoute = { path: '/history' }
            break
        }
        this.$router.push(this.nextRoute)
      }
    },
    setupOneSignal(credentials, userID) {
      if (this.$q.platform.is.capacitor) {
        window['plugins'].OneSignal.login(userID)
        window['plugins'].OneSignal.User.addEmail(credentials.email)
        window['plugins'].OneSignal.User.addTag('sw_user_id', userID)
      }
    },
    handleLoginSuccess(credentials, decodedToken, isAutoLogin = false) {
      this.setupOneSignal(credentials, String(decodedToken.sub))
      if (!isAutoLogin && credentials.provider === 'MSAL') {
        const expiresAt = dayjs().add(1, 'day').format('YYYY-MM-DD HH:mm:ss')
        credentials.expiresAt = expiresAt
      }
      const oldCredentials = this.$q.localStorage.getItem('credentials') // NOTE: this is to keep the old credentials expiration date in case isAutoLogin is true
      this.$q.localStorage.set('credentials', { ...oldCredentials, ...credentials })
      eventBus.$emit('userConnected')
    },
    handleReset() {
      eventBus.$emit('reset')
      setTimeout(() => window.location.reload(), 2000)
    },
    async authenticate(provider, email, password, isAutoLogin = false) {
      // NOTE: Handle user change
      const isDifferentUser =
        this.$q.localStorage.has('credentials') &&
        this.$q.localStorage.getItem('credentials').email !== email

      if (isDifferentUser) {
        eventBus.$emit('reset')
      }

      // NOTE: Authenticate
      var actionName = ''
      var credentials = {}

      switch (provider) {
        case 'MSAL':
          actionName = 'reporter/msalAuthenticate'
          credentials = {
            email: email,
            msalToken: password,
            provider: provider
          }
          break
        case 'SoWell':
          actionName = 'reporter/authenticate'
          credentials = {
            email: email,
            password: password,
            provider: provider
          }
          break
      }

      this.$q.loading.show({ delay: 0 })
      this.$store
        .dispatch(actionName, {
          credentials: credentials,
          device: this.device,
          appVersion: this.appVersion
        })
        .then((reporter) => {
          if (!isAutoLogin) {
            this.$q.localStorage.set(
              'lastResetDate',
              reporter.company['last-reset-date']
            )
          }
          const localResetDate = this.$q.localStorage.has('lastResetDate')
            ? this.$q.localStorage.getItem('lastResetDate')
            : false
          const isResetNeeded =
            !localResetDate ||
            localResetDate !== reporter.company['last-reset-date']
          if (isResetNeeded) {
            this.$q.localStorage.set(
              'lastResetDate',
              reporter.company['last-reset-date']
            )
            this.handleReset()
          } else {
            const DECODED_TOKEN = this.$helpers.decodeToken(
              this.$store.getters['reporter/token']
            )
            this.handleLoginSuccess(credentials, DECODED_TOKEN, isAutoLogin)
            this.handleLoginRedirect(
              DECODED_TOKEN.company_hp,
              DECODED_TOKEN.roles
            )
          }
        })
        .catch((err) => {
          this.$q.loading.hide()
          console.error(actionName + ' error', err)
        })
        .finally(() => {
          this.$q.loading.hide()
        })
    }
  }
}
</script>

<style lang="stylus">
.button-width {
  max-width: 300px
}

.separator {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 20px 0 20px;
}

.title{
  margin: 0 0 17px;
}

.separator::before,
.separator::after {
  content: '';
  flex: 1;
}

.separator:not(:empty)::before {
  margin-right: 0.25em;
}

.separator:not(:empty)::after {
  margin-left: 0.25em;
}

.bsk-btn {
  width: 100%;
  font-size: 15px;
  font-weight: normal;
  line-height: 0;
  touch-action: manipulation;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  background-image: none;
  color: #5e5e5e;
  background-color: white;
  border: 1px solid #dcd9d9;
  border-radius: 0;
  display: flex;
  flex-direction: row;
}

.bsk-btn span {
  text-transform: none;
}

/* Same footprint as the Microsoft button (.button-width + .bsk-btn), only the
   label is in primary. Declared after .bsk-btn so it wins on color. */
.first-login-button {
  color: #40c4ff;
}

.x-icon {
  position: absolute;
  left: 10px;
  top: 0;
  bottom: 0;
  margin: auto;
}

.x-alt {
  color: #777;
}

.sw-login-clear {
  margin-top: 20px;
  color: #808080;
}

.forgottenPass {
  margin-top: 0px !important;
  margin-left: -3px !important;
  cursor: pointer;
  color: #40c4ff
}

.iconInfoReset {
  margin-left: 3px
}
</style>
